import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';

import { PanelTitle } from 'ui';
import { NewsFeedSettings, EmailNotifications } from 'modules/common';
import { useSelector, useDispatch } from 'react-redux';

import isEqualWith from 'lodash/isEqualWith';
import { initialSettingsParser } from 'shared/parsers';
import { getShallowObjectsArrayDifference } from 'shared/functions';
import { savePreferencesAction, getPreferencesAction } from 'store/newsFeed/newsFeedActions';
import { Settings as NotificationsSettings } from 'modules/notificationsCentre/components/Settings';
import {
  fetchUserNotificationsSettings,
  updateNotificationsSettings,
} from 'modules/notificationsCentre/services/notificationsService';
import { parseSettingsData, parseSettingsDataToUpdate } from 'modules/notificationsCentre/parsers';

const useStyle = makeStyles((theme) => ({
  right: {
    float: 'right',
  },
  saveButton: {
    marginLeft: theme.spacing(1),
  },
}));

const notificationsSettingsId = 'notifications-settings';

const settingsVisibility = {
  newsFeed: false,
  newsletter: false,
  email: false,
  notifications: true,
};

const NewsCentre = () => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const didUnmount = useRef(false);

  const { preferences, defaultPrefsSet } = useSelector(({ newsFeed }) => newsFeed);

  const [initialSettings, setInitialSettings] = useState(initialSettingsParser(preferences));
  const [settings, setSettings] = useState(initialSettings);
  const [isSettingsChanged, setIsSettingsChanged] = useState(false);
  const [notificationsSettings, setNotificationsSettings] = useState();
  const [initialNotificationsSettings, setInitialNotificationsSettings] = useState();
  const [isNotificationsSettingsChanged, setIsNotificationsSettingsChanged] = useState(false);
  const [isNotificationsSettingsLoading, setIsNotificationsSettingsLoading] = useState(true);
  const [pristine, setPristine] = useState(true);
  const [didSettingsChange, setDidSettingsChange] = useState(false);

  const checkSettingsChanged = () =>
    !isEqualWith(initialSettings, settings, (first, second) => {
      if (Array.isArray(first) && Array.isArray(second)) {
        return first.length === second.length && first.every((element) => second.includes(element));
      }
      return undefined;
    });

  useEffect(() => {
    if (!defaultPrefsSet) {
      setInitialSettings(initialSettingsParser(preferences));
    }
    setSettings(initialSettingsParser(preferences));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences]);

  useEffect(() => {
    let isMounted = true;

    if (settingsVisibility.notifications) {
      fetchUserNotificationsSettings().then((result) => {
        if (!isMounted) {
          return;
        }

        setInitialNotificationsSettings(result);
        setNotificationsSettings(result);
        setIsNotificationsSettingsLoading(false);
      });
    }

    return () => {
      isMounted = false;
      didUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    const _isSettingsChanged = checkSettingsChanged(settings);
    setIsSettingsChanged(_isSettingsChanged);

    let _isNotificationsSettingsChanged = false;
    if (settingsVisibility.notifications) {
      _isNotificationsSettingsChanged = !isEqualWith(
        initialNotificationsSettings,
        notificationsSettings,
      );
    }
    setIsNotificationsSettingsChanged(_isNotificationsSettingsChanged);

    setDidSettingsChange(_isSettingsChanged || _isNotificationsSettingsChanged);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, initialNotificationsSettings, notificationsSettings]);

  const handleNewsFeedSettingsChanged = (updatedSettings) => {
    setPristine(false);
    setSettings((currentSettings) => ({
      ...currentSettings,
      newsFeed: updatedSettings.newsFeed,
    }));
  };

  const handleNewsletterSettingsChanged = (updatedSettings) => {
    setPristine(false);
    setSettings((currentSettings) => ({
      ...currentSettings,
      newsletter: updatedSettings.newsletter,
    }));
  };

  const handleEmailSettingsChanged = (emailSettings) => {
    setPristine(false);
    setSettings((currentSettings) => ({
      ...currentSettings,
      emailNotifications: emailSettings,
    }));
  };

  const handleNotificationsSettingsChanged = (id, key, updatedSetting) => {
    setPristine(false);
    setNotificationsSettings(
      notificationsSettings.map((data) => {
        const { _id: currentId } = data;

        if (id === currentId) {
          return {
            ...data,
            [key]: updatedSetting,
          };
        }

        return data;
      }),
    );
  };

  const saveChanges = () => {
    if (isSettingsChanged) {
      dispatch(savePreferencesAction(settings));
    }

    if (settingsVisibility.notifications && isNotificationsSettingsChanged) {
      const changedData = getShallowObjectsArrayDifference(
        initialNotificationsSettings,
        notificationsSettings,
      );

      updateNotificationsSettings({ data: parseSettingsDataToUpdate(changedData) }).then(() => {
        if (!didUnmount.current) {
          setInitialNotificationsSettings(notificationsSettings);
        }
      });
    }
  };

  const setToDefault = () => {
    dispatch(getPreferencesAction(true));
  };

  const discardChanges = () => {
    setPristine(true);
  };

  return (
    <>
      {settingsVisibility.newsFeed && (
        <>
          <PanelTitle>{t('News feed')}</PanelTitle>
          <NewsFeedSettings
            newsSettingsChanged={handleNewsFeedSettingsChanged}
            pristine={pristine}
          />
        </>
      )}
      {settingsVisibility.newsletter && (
        <>
          <PanelTitle>{t('Newsletter')}</PanelTitle>
          <NewsFeedSettings
            newsletterEnabled
            newsSettingsChanged={handleNewsletterSettingsChanged}
            pristine={pristine}
          />
        </>
      )}
      {settingsVisibility.email && (
        <>
          <PanelTitle>{t('Email notifications')}</PanelTitle>
          <EmailNotifications
            emailSettingsChanged={handleEmailSettingsChanged}
            pristine={pristine}
          />
        </>
      )}
      {settingsVisibility.notifications && (
        <>
          <PanelTitle id={notificationsSettingsId}>{t('Notifications settings')}</PanelTitle>
          <NotificationsSettings
            onNotificationsSettingsChange={handleNotificationsSettingsChanged}
            settings={
              !isNotificationsSettingsLoading ? parseSettingsData(notificationsSettings) : undefined
            }
            isLoading={isNotificationsSettingsLoading}
          />
        </>
      )}
      <Button variant="outlined" onClick={setToDefault}>
        {t('Set to default')}
      </Button>
      <div className={classes.right}>
        <Button variant="outlined" onClick={discardChanges}>
          {t('Discard changes')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.saveButton}
          disabled={!didSettingsChange}
          onClick={saveChanges}
        >
          {t('Save changes')}
        </Button>
      </div>
    </>
  );
};

export { NewsCentre };
